import { computed, inject, Injectable } from '@angular/core'
import { _pick } from '@naturalcycles/js-lib'
import {
  ActivationChannel,
  ApplyDiscountInput,
  BackendResponseFMResp,
  CampaignCodeBM,
  CartDiscount,
  CartDiscountType,
  CartInput,
  CartItem,
  CartItemInput,
  CartState,
  Currency,
  DiscountCodeFM,
  ExperimentalPrice,
  HardwareId,
  isRollingTrialKey,
  Product,
  ProductKey,
  ProductType,
  ProductVariant,
  thermometerKeys,
} from '@naturalcycles/shared'
import { MixpanelService } from '@src/app/core/services/analytics/mixpanel.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { MixpanelEvent } from '@src/app/shared/typings/analytics.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { emptyCartState } from '../util/cart.util.js'
import { api } from './api.service.js'

@Injectable({ providedIn: 'root' })
export class CartService {
  private store = inject(SignalStore)
  private mixpanelService = inject(MixpanelService)

  public netTotal = computed<ExperimentalPrice>(() => {
    const cart = this.store.$cart()
    return this.getCartNetTotal(cart)
  })

  public grossTotal = computed<ExperimentalPrice>(() => {
    const cart = this.store.$cart()
    return this.getCartGrossTotal(cart)
  })

  public taxTotal = computed<ExperimentalPrice | null>(() => {
    const cart = this.store.$cart()
    return this.getCartTaxTotal(cart)
  })

  public verificationAmount = computed<ExperimentalPrice>(() => {
    const cart = this.store.$cart()
    return ExperimentalPrice.fromCents(cart.threeDSecureVerificationAmount, cart.currency)
  })

  /**
   * @description
   * We use this method on the SECRET PAGE to build a predefined cart
   *
   * @example Pharmacy codes (encrypted)
   * @example Trial codes (not encrypted)
   *
   * @param {ApplyDiscountInput} body contains the code which product bundle to create
   * @returns {CartState} the predefined cart
   */
  async createPredefinedCart(
    input: Pick<ApplyDiscountInput, 'code' | 'referrerId'>,
  ): Promise<CartState> {
    const { backendResponse } = await api.post<BackendResponseFMResp>(
      `carts/discount/predefined-cart`,
      {
        json: input,
      },
    )
    if (backendResponse.cart?.discount?.code) {
      void this.mixpanelService.trackEvent(MixpanelEvent.ApplyDiscount, {
        code: backendResponse.cart.discount.code.toLowerCase(),
        encryptedCode: backendResponse.cart.discount.encryptedCode,
        type: CartDiscountType.PredefinedCart,
        page: Page.code,
      })
    }

    return backendResponse.cart!
  }

  /**
   * @description
   * We use this method at CHECKOUT to validate a discount code before applying it to the cart.
   *
   * @note
   * - If the discount code's conditions aren't met, an exception will be thrown with the code DISCOUNT_CODE_VALIDATION_FAILURE.
   * - If zero discounts can be applied, the {@link CartState.discountStatus} will be {@link CartDiscountStatus.Mismatch}.
   * - If at least one discount can be applied, the {@link CartState.discountStatus} will be {@link CartDiscountStatus.Applied}.
   *
   * In the case that there's a 'Mismatch' between the user's product selection and the discount code's product offering
   * We ask the user to replace their cart with the products that the discount code is intended for.
   *
   * @param {ApplyDiscountInput} body contains the discount to validate
   * @returns {CartState} A new cart state with the discount applied
   */
  async validateDiscount(input: ApplyDiscountInput): Promise<CartState> {
    return await api.put(`carts/discount/validate`, {
      json: input,
    })
  }

  async getDiscountItems(code: string, referrerId?: string): Promise<CartState> {
    let searchParams = {}
    if (referrerId) searchParams = { referrerId }

    return await api.get(`carts/discount/${code}`, { searchParams })
  }

  getDiscount(): CartDiscount | undefined {
    return this.store.$cart().discount
  }

  emptyCart(): void {
    const { pricingCurrency } = this.store.$userLocale()
    const emptyCart = emptyCartState(pricingCurrency as Currency)
    this.store.$cart.set(emptyCart)
  }

  hasProduct(key: ProductKey): boolean {
    return this.getCartItems().some(i => i.key === key)
  }

  async selectDevice(
    hwId: HardwareId,
    productKey: ProductKey,
    ownsDevice?: boolean,
  ): Promise<void> {
    this.store.$hardware.update(s => ({
      ...s,
      hwId,
      ownsDevice,
    }))

    const selectedProduct = this.store.$product().items.find(i => i.key === productKey)

    // Clear out any existing device selection
    const updatedItems = this.getCartItems().filter(i => !i.hwId) as CartItemInput[]
    const variant = ownsDevice ? ProductVariant.ownsDevice : ProductVariant.wantsDevice
    const items = [
      ...updatedItems,
      {
        key: productKey,
        quantity: 1,
        ...(selectedProduct?.variants?.includes(variant) && { variant }),
      },
    ]

    await this.update({
      hwId,
      items,
      discount: this.getDiscount(),
    })
  }

  async update(input: CartInput): Promise<CartState> {
    const { backendResponse } = await api.post<BackendResponseFMResp>('carts/', { json: input })
    return backendResponse.cart!
  }

  getCartItems(): CartItem[] {
    return this.store.$cart().items
  }

  getCartItemInputs(): CartItemInput[] {
    return this.getCartItems().map(item => _pick(item, ['key', 'quantity', 'variant']))
  }

  getSubscriptionType(specificCart?: CartState): ProductKey | undefined {
    const cart = specificCart || this.store.$cart()
    const subscription = cart.items.find((item: CartItem) => item.type === ProductType.SUBSCRIPTION)

    return subscription?.key
  }

  getItemsExceptForMeasuringDevice(): CartItem[] {
    return this.store
      .$cart()
      .items.filter(
        i =>
          i.key !== ProductKey.BT_THERMOMETER &&
          i.key !== ProductKey.THERMOMETER &&
          i.key !== ProductKey.T3_THERMOMETER &&
          i.key !== ProductKey.OURA_RING_DISCOUNT &&
          i.key !== ProductKey.APPLE_WATCH,
      )
  }

  /**
   * We use this method to determine whether we should ask the user for a shipping address.
   */
  requiresShippingAddress(cart: CartState): boolean {
    return cart.items.some(item => item.type === ProductType.SHOP)
  }

  /**
   * Convenience method for checking oura rolling trial
   */
  isOuraRollingTrial(cart: CartState): boolean {
    return (
      cart.hwId === HardwareId.OURA &&
      !cart.items.some(i => i.type === ProductType.SHOP) &&
      cart.items.some(i => isRollingTrialKey(i.key))
    )
  }

  /**
   * Convinient method for checking apple rolling trial
   */
  isAppleRollingTrial(cart: CartState): boolean {
    return (
      cart.hwId === HardwareId.APPLE_WATCH &&
      !cart.items.some(i => i.type === ProductType.SHOP) &&
      cart.items.some(i => isRollingTrialKey(i.key))
    )
  }

  hasRollingTrial(cart: CartState): boolean {
    return cart.items.some(i => isRollingTrialKey(i.key))
  }

  /**
   * Check if cart contains recurring subscription.
   */
  isRecurringSubscription(cart: CartState): boolean {
    return cart.items.some(i => i.type === ProductType.SUBSCRIPTION && i.recurring)
  }

  /**
   * @description
   * When you hear team member's talk about 'encrypted codes', 'prepaid codes' or 'pharmacy codes',
   * they're referring to a physical NC offering purchased over the counter at retailers, such as Walmart.
   * The most common example is a pre-packaged thermometer that comes with a 6-month fixed membership.
   *
   * @note
   * - The Plan's page is not relevant as the code corresponds to a fixed 6-month membership.
   * - The Delivery address is not needed since the item has been obtained in person.
   * - The Billing address is unnecessary as payment was made at the point of purchase.
   * - The Payment's Page is not applicable since the subscription doesn't auto-renew (it's a 6-month fixed membership).
   */
  hasActivationCodeApplied(cart: CartState): boolean {
    const pharmacyCode = !!cart.discount?.encryptedCode && cart.netTotalAmount === 0

    // The 'e2eTestBundle100' is meant to emulate an encrypted pharmacy code
    // But for practical reasons we can't encrypt it when using e2e tests
    // Which is why we have it hardcoded here
    return pharmacyCode || cart.discount?.code === 'e2eTestBundle100'
  }

  hasClearblueCodeApplied(discountCode: DiscountCodeFM | undefined): boolean {
    return discountCode?.activationChannel === ActivationChannel.CLEARBLUE
  }

  getSubscriptions(items: Product[]): Product[] {
    return items.filter(i => i.type === ProductType.SUBSCRIPTION)
  }

  /**
   * True if cart has a discount code that's hardware upgradable.
   * False if cart does not have a hardware or have a code applied.
   * False for all oura traffic.
   */
  hardwareUpgradeAvailable(cart: CartState): boolean {
    const hasNCCodeWithHw =
      cart.hwId !== HardwareId.OURA &&
      cart.hwId !== HardwareId.APPLE_WATCH &&
      !!cart.discount &&
      !cart.discount.encryptedCode &&
      this.getCartItems().some(i => thermometerKeys.includes(i.key))

    return hasNCCodeWithHw
  }

  async getOuraCodeForUser(accountId: string): Promise<CampaignCodeBM | undefined> {
    const campaignCode: { code: CampaignCodeBM | undefined } = await api.get(
      `campaigncode/${accountId}`,
    )
    return campaignCode.code
  }

  public getCartNetTotal(cart: CartState): ExperimentalPrice {
    return ExperimentalPrice.fromCents(cart.netTotalAmount, cart.currency)
  }

  public getCartGrossTotal(cart: CartState): ExperimentalPrice {
    return ExperimentalPrice.fromCents(cart.grossTotalAmount, cart.currency)
  }

  public getCartTaxTotal(cart: CartState): ExperimentalPrice | null {
    if (!cart.taxTotalAmount) return null
    return ExperimentalPrice.fromCents(cart.taxTotalAmount, cart.currency)
  }
}
